import { parsePhoneNumberFromString } from "libphonenumber-js";
import type { Offer } from "@/interfaces/p2p";
import { toast } from "sonner";

export const normalizeCurrency = (currency: string) =>
  currency?.startsWith("t") ? currency.slice(1) : currency;

export const removeTrailingToken = (name: string): string => {
  return name?.replace(/\s+token$/i, "");
};

// Format balance
export const formatBalance = (balance: string | number, decimals: number) => {
  const num = Number(balance);

  if (isNaN(num)) return "0";

  return (num / 10 ** decimals).toLocaleString();
};

export const formatCurrency = (value: number | string | null | undefined) => {
  const num = Number(value);

  if (isNaN(num)) return "0.00"; // or "--" depending on UX

  if (num >= 1_000_000_000) return `${(num / 1_000_000_000).toFixed(2)}B`;
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(2)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(2)}K`;

  return num.toFixed(2);
};

export const formatTime = (seconds: number) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;

  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

export const copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text);
  toast.success("Copied!", {
    description: "Copied to clipboard",
  });
};

export const formatDate = (dateString: string) => {
  return new Intl.DateTimeFormat("en-US", {
    dateStyle: "long",
    timeStyle: "short",
  }).format(new Date(dateString));
};

import {
  format,
  formatDistanceToNow,
  isToday,
  isYesterday,
  differenceInHours,
} from "date-fns";
import type { UserProfile } from "@/interfaces/profile";

export const formatChatTime = (dateInput: string | Date) => {
  const date = new Date(dateInput);
  const now = new Date();

  const hoursDiff = differenceInHours(now, date);

  // 🔹 very recent → relative seconds/minutes
  if (hoursDiff < 1) {
    return formatDistanceToNow(date, { addSuffix: true }); // "5 minutes ago"
  }

  // 🔹 same day → show time
  if (isToday(date)) {
    return `Today ${format(date, "HH:mm")}`;
  }

  // 🔹 yesterday
  if (isYesterday(date)) {
    return `Yesterday ${format(date, "HH:mm")}`;
  }

  // 🔹 within a week → short date + time
  if (hoursDiff < 24 * 7) {
    return format(date, "EEE HH:mm"); // "Mon 14:30"
  }

  // 🔹 older → full date
  return format(date, "dd MMM yyyy • HH:mm");
};

export function maskAccountNumber(accountNumber: string) {
  if (accountNumber.length <= 4) return accountNumber;

  return "*".repeat(accountNumber.length - 4) + accountNumber.slice(-4);
}

export function getPaymentIcon(type: string) {
  switch (type) {
    case "BANK_TRANSFER":
      return "🏦";

    case "MOBILE_MONEY":
      return "📱";

    case "E_WALLET":
      return "💳";

    default:
      return "💰";
  }
}

export const normalizePhoneForUI = (phone?: string) => {
  if (!phone) return "";

  const parsed = parsePhoneNumberFromString(phone);

  // fallback safe cleanup
  if (!parsed) return phone.replace(/\s+/g, "");

  return parsed.number; // E.164 clean version
};

export function safeDecimal(value: any) {
  if (value === null || value === undefined || value === "") return undefined;

  const num = Number(value);

  if (Number.isNaN(num)) return undefined;

  // FIX floating precision issues
  return num.toFixed(10).replace(/\.?0+$/, "");
}

export const checkPasswordSetup = (user: UserProfile) => {
  if (!user || !user?.setting) return false;

  if (!user?.setting?.twoFAEnabled || !user?.isSecurityCodeVerified)
    return false;

  return true;
};

export const formatEvmHumanBalance = (
  amount: string | number | bigint,
  isNative: boolean,
): string => {
  const value = Number(amount);

  if (!Number.isFinite(value)) {
    throw new Error("Invalid amount");
  }

  if (value === 0) {
    return "0";
  }

  const abs = Math.abs(value);

  // Decimal precision based on asset type
  const decimals = isNative ? 6 : 2;

  // 100,000 and above -> compact notation
  const units = [
    { value: 1e30, suffix: "No" }, // Nonillion
    { value: 1e27, suffix: "Oc" }, // Octillion
    { value: 1e24, suffix: "Sp" }, // Septillion
    { value: 1e21, suffix: "Sx" }, // Sextillion
    { value: 1e18, suffix: "Qi" }, // Quintillion
    { value: 1e15, suffix: "Qa" }, // Quadrillion
    { value: 1e12, suffix: "T" }, // Trillion
    { value: 1e9, suffix: "B" }, // Billion
    { value: 1e6, suffix: "M" }, // Million
    { value: 1e5, suffix: "K" }, // 100,000+
  ];

  // 100,000 and above: 100K, 100.79K, 1.25M, etc.
  for (const unit of units) {
    if (abs >= unit.value) {
      const formatted = (value / unit.value).toFixed(2).replace(/\.?0+$/, "");

      return `${formatted}${unit.suffix}`;
    }
  }

  // Below 100,000:
  // Use comma separators, while preserving decimal precision
  // according to whether the asset is native or ERC-20.
  return value.toLocaleString("en-US", {
    minimumFractionDigits: isNative ? 0 : 2,
    maximumFractionDigits: decimals,
  });
};
